// Vacation management TypeScript interfaces

/**
 * Represents a single vacation balance period
 */
export interface VacationBalance {
  employeeId: string
  tenantId: string
  periodYear: number
  acquisitionStartDate: string // ISO 8601
  acquisitionEndDate: string // ISO 8601

  // Aliases for backward compatibility
  acquisitionStart?: string // Alias for acquisitionStartDate
  acquisitionEnd?: string // Alias for acquisitionEndDate

  daysEarned: number
  totalDaysEarned?: number // Alias for daysEarned
  daysUsed: number
  daysScheduled: number
  daysAvailable: number
  expirationDate: string // ISO 8601
  daysAtRisk: number
  isExpiringSoon: boolean
  canSellThird?: boolean // Whether employee can sell 1/3 of vacation days
}

/**
 * API response for vacation balance
 */
export interface VacationBalanceResponse {
  balances: VacationBalance[]
}

/**
 * Vacation request status
 */
export type VacationRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED'

/**
 * Represents a vacation request
 */
export interface VacationRequest {
  requestId: string
  employeeId: string
  tenantId: string
  startDate: string // ISO 8601
  endDate: string // ISO 8601
  totalDays: number
  type: VacationType
  status: VacationRequestStatus
  requestedAt: string // ISO 8601
  notes?: string
  approvedBy?: string
  approvedAt?: string
  rejectionReason?: string
}

/**
 * API response for vacation requests list
 */
export interface VacationRequestsResponse {
  requests: VacationRequest[]
  pagination?: {
    nextToken?: string
    itemCount: number
  }
}

/**
 * Alert severity levels
 */
export type AlertSeverity = 'critical' | 'warning' | 'info'

/**
 * Vacation alert for UI display (employee-facing)
 */
export interface EmployeeVacationAlert {
  id: string
  severity: AlertSeverity
  message: string
  dismissible: boolean
}

// ============================================================================
// VAC-8: Manager Approval Queue and Team Calendar Types
// ============================================================================

/**
 * Vacation request type (enum version for VAC-7)
 */
export enum VacationRequestType {
  FULL = 'FULL',
  SPLIT = 'SPLIT',
  SELL_THIRD = 'SELL_THIRD'
}

/**
 * Vacation request type (legacy type alias - kept for backward compatibility)
 */
export type VacationType = 'FULL' | 'SPLIT' | 'SELL_THIRD'

/**
 * Extended vacation request for approval queue (manager view)
 */
export interface ApprovalQueueItem {
  requestId: string
  employeeId: string
  employeeName: string
  employeeAvatar?: string
  employeeDepartment?: string
  employeeRole?: string
  employeeHireDate?: string
  tenantId: string
  managerId: string
  startDate: string // ISO 8601
  endDate: string // ISO 8601
  totalDays: number
  type: VacationType
  status: VacationRequestStatus
  submittedAt: string // ISO 8601
  approvedAt?: string
  approvedBy?: string
  approvalNotes?: string
  rejectionReason?: string
  employeeNotes?: string
  periodYear: number
  daysAgo: number // Calculated from submittedAt
  balanceExpiringSoon: boolean // Flag if balance expires < 60 days
}

/**
 * API response for approval queue
 */
export interface ApprovalQueueResponse {
  items: ApprovalQueueItem[]
  count: number
  page?: number
  pageSize?: number
  totalPages?: number
}

/**
 * Calendar entry for team vacation view
 */
export interface CalendarEntry {
  requestId: string
  employeeId: string
  employeeName: string
  employeeAvatar?: string
  startDate: string // ISO 8601
  endDate: string // ISO 8601
  totalDays: number
  type: VacationType
  status: 'PENDING' | 'APPROVED'
}

/**
 * API response for team vacation calendar
 */
export interface CalendarResponse {
  items: CalendarEntry[]
  count: number
  dateRange: {
    startDate: string
    endDate: string
  }
}

/**
 * Payload for approving a vacation request
 */
export interface ApproveRequestPayload {
  approvalNotes?: string
}

/**
 * Payload for rejecting a vacation request
 */
export interface RejectRequestPayload {
  rejectionReason: string
}

/**
 * Team coverage data for a specific date
 */
export interface TeamCoverageData {
  date: string
  employeesOnVacation: number
  totalEmployees: number
  coveragePercentage: number
  employees: string[] // Employee names
}

// ============================================================================
// VAC-7: Vacation Request Submission Types
// ============================================================================

/**
 * Input for creating a new vacation request
 */
export interface CreateVacationRequestInput {
  startDate: string // ISO 8601 (YYYY-MM-DD)
  endDate: string // ISO 8601 (YYYY-MM-DD)
  type: VacationRequestType
  notes?: string
}

/**
 * Validation result for vacation request
 */
export interface ValidationResult {
  valid: boolean
  errors: string[]
}

/**
 * Form draft state (for localStorage)
 */
export interface VacationRequestDraft {
  startDate: string | null
  endDate: string | null
  type: VacationRequestType | null
  notes: string
  savedAt: string // ISO 8601 timestamp
}
